import { useCallback, useEffect, useRef, useState } from "react";

export type Stage =
  | "novo"
  | "engajado"
  | "descoberta"
  | "qualificado"
  | "match"
  | "visita_agendada"
  | "visita_realizada"
  | "proposta"
  | "ganho"
  | "perdido";

export const STAGE_LABEL: Record<Stage, string> = {
  novo: "Novo",
  engajado: "Engajado",
  descoberta: "Descoberta",
  qualificado: "Qualificado",
  match: "Recebeu imóvel",
  visita_agendada: "Visita agendada",
  visita_realizada: "Visitou",
  proposta: "Proposta",
  ganho: "Ganho",
  perdido: "Perdido",
};

export interface QueueItem {
  handoff_id: string;
  conversation_id: string;
  lead_id: string;
  lead_name: string | null;
  lead_phone: string;
  stage: Stage;
  fit_score: number;
  temp_score: number;
  reason: string;
  urgency: "baixa" | "normal" | "alta";
  summary: string | null;
  waiting_minutes: number;
  control: "ai" | "pending_human" | "human";
  assigned_broker: string | null;
  last_message: string | null;
}

export interface BoardLead {
  id: string;
  name: string | null;
  phone_e164: string;
  stage: Stage;
  fit_score: number;
  temp_score: number;
  hours_in_stage: number;
  source: string;
  control: "ai" | "pending_human" | "human";
  last_inbound_at: string | null;
}

export interface Metrics {
  leads_hoje: number;
  leads_7d: number;
  qualificados_7d: number;
  esperando_humano: number;
  primeira_resposta_mediana_s: number | null;
  minutos_ate_assumir: number | null;
  handoffs_justos_pct: number | null;
}

export interface Fact {
  field: string;
  value: unknown;
  confidence: number;
  origin: string;
  created_at: string;
}

export interface Message {
  id: string;
  author: "lead" | "ai" | "human" | "system";
  body: string | null;
  kind: string;
  /** received | queued | sent | delivered | read | failed */
  status: string;
  error: string | null;
  created_at: string;
  delivered_at?: string | null;
  read_at?: string | null;
  /** Tem arquivo guardado: a conversa mostra a foto ou o player. */
  tem_midia?: boolean;
  mime?: string | null;
  segundos?: number | null;
}

export interface TimelineItem {
  kind: "mensagem" | "fato" | "etapa" | "handoff" | "imovel";
  at: string;
  author: string | null;
  body: string | null;
  detail: string | null;
}

export interface LeadDetail {
  lead: {
    id: string;
    name: string | null;
    phone_e164: string;
    stage: Stage;
    fit_score: number;
    temp_score: number;
    source: string;
    opted_out: boolean;
    created_at: string;
    stage_since: string;
    conversation_id: string | null;
    control: "ai" | "pending_human" | "human" | null;
    assigned_broker: string | null;
  };
  facts: Fact[];
  timeline: TimelineItem[];
  messages: Message[];
  fit: FitComponent[];
  /** O que o site mandou. Nulo quando o lead não veio de formulário. */
  formulario: FormularioRecebido | null;
}

export interface FitComponent {
  ordem: number;
  componente: string;
  pontos: number;
  maximo: number;
  detalhe: string;
}

export interface Channel {
  id: string;
  label: string;
  kind: string;
  phone_e164: string | null;
  status: "disconnected" | "qr" | "connecting" | "connected" | "banned";
  status_detail: string | null;
  /** String crua do QR. A API nunca devolve QR vencido. */
  qr: string | null;
  active: boolean;
  notes: string | null;
  owner_name: string | null;
  last_seen_at: string | null;
  conversations: number;
}

export type AlertEvent =
  | "handoff"
  | "lead_novo"
  | "qualificado"
  | "visita"
  | "canal_caiu"
  | "sistema"
  | "resumo_diario";

export interface AlertType {
  evento: AlertEvent;
  rotulo: string;
  descricao: string;
}

export interface AlertTarget {
  id: string;
  label: string;
  phone_e164: string;
  broker_id: string | null;
  broker_name: string | null;
  events: AlertEvent[];
  channel_id: string | null;
  channel_label: string | null;
  quiet_from: number | null;
  quiet_to: number | null;
  active: boolean;
}

export type TurnStep =
  | { tipo: "ferramenta"; nome: string; entrada: unknown; saida: string; ms: number }
  | { tipo: "texto"; texto: string };

export interface AgentTurn {
  id: string;
  trigger: "mensagem" | "followup";
  model: string | null;
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  thinking: string | null;
  steps: TurnStep[];
  reply: string | null;
  chunks: number;
  outcome: Record<string, unknown>;
  aborted: string | null;
  error: string | null;
  tokens_in: number;
  tokens_cached: number;
  tokens_out: number;
  tokens_cache_write: number;
  cost_usd: number;
  /** Idas a API neste turno. Nulo em turnos anteriores a 041. */
  chamadas: number | null;
  /** Alguém marcou esta resposta como ruim. */
  ruim: boolean;
  ruim_comentario: string | null;
}

/** Uma resposta marcada como ruim, esperando a revisão. */
export interface RespostaRuim {
  id: string;
  turn_id: string;
  lead_id: string | null;
  lead_name: string | null;
  comentario: string | null;
  por: string | null;
  created_at: string;
  resposta: string | null;
  antes: string | null;
  modelo: string | null;
}

export interface LiveConversation {
  conversation_id: string;
  lead_id: string;
  lead_name: string | null;
  lead_phone: string;
  stage: string;
  fit_score: number;
  temp_score: number;
  control: "ai" | "pending_human" | "human";
  assigned_broker: string | null;
  channel_label: string;
  last_message: string | null;
  last_message_author: string | null;
  last_message_at: string | null;
  quiet_seconds: number | null;
  /** Turno em andamento agora. */
  thinking_since: string | null;
  followup_at: string | null;
  facts_count: number;
  turns_count: number;
  usd: number | null;
  /** "digitando" / "gravando", vindo do Redis. Expira sozinho. */
  presence: string | null;
}

export interface PropertyStatusInfo {
  status: string;
  rotulo: string;
  ajuda: string;
}

export interface PropertyRow {
  id: string;
  external_id: string | null;
  title: string;
  status: string;
  development: string | null;
  unit: string | null;
  neighborhood: string | null;
  city: string | null;
  price: number | null;
  bedrooms: number | null;
  parking: number | null;
  sales_notes: string | null;
  fotos: number;
}

export interface Attachment {
  id: string;
  kind: "foto" | "documento";
  filename: string;
  mime: string;
  bytes: number;
  position: number;
}

export type KnowledgeKind =
  | "regiao"
  | "financiamento"
  | "empreendimento"
  | "politica"
  | "faq";

export interface KnowledgeKindInfo {
  kind: KnowledgeKind;
  rotulo: string;
  ajuda: string;
}

export interface Agendamento {
  id: string;
  lead_id: string;
  lead_name: string | null;
  lead_phone: string;
  modo: "ligacao" | "video" | "presencial";
  quando_texto: string;
  quando: string | null;
  status: string;
  contexto: string | null;
  created_at: string;
}

export interface Settings {
  objetivo: "agendar" | "qualificar";
  disponibilidade: string | null;
  agentName: string;
  companyName: string;
  city: string;
  policy: string | null;
  catalogo: "on" | "off";
  aceitarNovosLeads: boolean;
  effort: "low" | "medium" | "high" | "xhigh" | "max";
  modelo: "claude-opus-5" | "claude-sonnet-5";
  tetoTotalUsd: number;
  tetoDiarioUsd: number;
  avisarSaldoUsd: number;
  transcricaoProvedor: "nenhum" | "groq" | "openai";
  expedienteDe: number;
  expedienteAte: number;
  abordagemDe: number;
  abordagemAte: number;
}

/** Gasto com o modelo e o que resta do teto. Ver a aba Ajustes. */
export interface Gasto {
  total: number;
  hoje: number;
  teto: number;
  tetoDiario: number;
  /** Nulo quando nao ha teto configurado -- sem teto nao existe saldo. */
  restante: number | null;
  avisarEm: number;
}

export interface KnowledgeRow {
  id: string;
  kind: KnowledgeKind;
  title: string;
  content: string;
  neighborhood: string | null;
  tags: string[];
  active: boolean;
  updated_at: string;
  /** Fotos anexadas -- o que a IA pode enviar quando o lead pedir para ver. */
  fotos: number;
  /** Lead deste anúncio recebe só a abertura e vai direto pro corretor. */
  direto_ao_corretor: boolean;
  /** Endereço público do material: vídeo, tour, página do imóvel. */
  link: string | null;
}

export interface FollowupRule {
  id: string;
  stage: string;
  attempt: number;
  delay_minutes: number;
  hook: string;
  active: boolean;
}

export interface ScheduledFollowup {
  id: string;
  lead_id: string;
  lead_name: string | null;
  lead_phone: string;
  stage_at_schedule: string;
  attempt: number;
  hook: string;
  run_at: string;
  minutes_until: number;
}

export interface FollowupData {
  fila: ScheduledFollowup[];
  stats: {
    agendados: number;
    enviados_7d: number;
    responderam_7d: number;
    cancelados_7d: number;
  };
  regras: FollowupRule[];
}

export interface Broker {
  id: string;
  name: string;
  phone_e164: string;
  role: string;
}

/** Numero que o atendimento nao enxerga -- ver a aba Ignorados. */
/** O que o site mandou pelo POST /api/formulario, cru. */
export interface FormularioRecebido {
  origem: string;
  payload: Record<string, unknown>;
  requested_at: string;
}

export interface Ignorado {
  id: string;
  phone_e164: string;
  label: string | null;
  created_at: string;
}

export async function get<T>(path: string): Promise<T> {
  const res = await fetch(`/api${path}`);
  if (!res.ok) throw new Error(`${res.status} em ${path}`);
  return res.json() as Promise<T>;
}

export async function post<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) throw new Error(`${res.status} em ${path}`);
  return res.json() as Promise<T>;
}

export async function patch<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) throw new Error(`${res.status} em ${path}`);
  return res.json() as Promise<T>;
}

export async function del<T>(path: string): Promise<T> {
  const res = await fetch(`/api${path}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`${res.status} em ${path}`);
  return res.json() as Promise<T>;
}

/**
 * Busca dados e reassina quando o servidor avisa que algo mudou.
 *
 * Sem polling: o WebSocket recebe o evento que nasceu de um trigger do
 * Postgres. Um painel de fila com atraso de segundos e um painel que o
 * corretor para de olhar -- e polling com varios corretores abertos e carga
 * constante no banco por nada.
 */
export function useLive<T>(
  path: string,
  watch: readonly string[],
): { data: T | null; error: string | null; reload: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const watchRef = useRef(watch);
  watchRef.current = watch;

  const reload = useCallback(() => {
    get<T>(path)
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((e: Error) => setError(e.message));
  }, [path]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    const unsubscribe = subscribe((event) => {
      if (watchRef.current.includes(event.table)) reload();
    });
    return unsubscribe;
  }, [reload]);

  return { data, error, reload };
}

interface AxisEvent {
  table: string;
  op: string;
  id: string;
  tenant: string;
}

type EventListener = (e: AxisEvent) => void;

let socket: WebSocket | null = null;
let listeners = new Set<EventListener>();
let reconnectDelay = 500;
export const connection = { online: false };

/**
 * Uma conexao para o app inteiro, compartilhada por todas as telas. Abrir um
 * socket por componente multiplicaria conexoes a cada navegacao.
 */
function ensureSocket(): void {
  if (socket && socket.readyState <= WebSocket.OPEN) return;

  const proto = location.protocol === "https:" ? "wss" : "ws";
  socket = new WebSocket(`${proto}://${location.host}/api/stream`);

  socket.onopen = () => {
    reconnectDelay = 500;
    connection.online = true;
    for (const l of listeners) l({ table: "__online", op: "", id: "", tenant: "" });
  };
  socket.onmessage = (ev) => {
    try {
      const e = JSON.parse(ev.data as string) as AxisEvent;
      for (const l of listeners) l(e);
    } catch {
      /* frame ilegivel: ignora em vez de derrubar a tela */
    }
  };
  socket.onclose = () => {
    connection.online = false;
    socket = null;
    if (listeners.size > 0) {
      setTimeout(ensureSocket, reconnectDelay);
      reconnectDelay = Math.min(15_000, reconnectDelay * 2);
    }
  };
  socket.onerror = () => socket?.close();
}

function subscribe(listener: EventListener): () => void {
  listeners.add(listener);
  ensureSocket();
  return () => {
    listeners.delete(listener);
  };
}

export function useConnection(): boolean {
  const [online, setOnline] = useState(connection.online);
  useEffect(() => {
    const tick = setInterval(() => setOnline(connection.online), 1000);
    const off = subscribe(() => setOnline(connection.online));
    return () => {
      clearInterval(tick);
      off();
    };
  }, []);
  return online;
}

/** "há 3 min", "há 2 h", "ontem" — o corretor pensa em tempo relativo. */
export function ago(iso: string | null): string {
  if (!iso) return "—";
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (min < 1) return "agora";
  if (min < 60) return `${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h`;
  const d = Math.round(h / 24);
  return d === 1 ? "ontem" : `${d} dias`;
}

export function formatPhone(e164: string): string {
  const d = e164.replace(/\D/g, "");
  if (d.length === 13) return `(${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
  if (d.length === 12) return `(${d.slice(2, 4)}) ${d.slice(4, 8)}-${d.slice(8)}`;
  return e164;
}
